import React, { useState, useEffect, useRef } from 'react';
import { X, Plus, Minus, Trash2, AlertCircle } from 'lucide-react';
import { superAdminAPI } from '../../services/api';
import { handleFormError, clearErrors, validateRequiredFields } from '../../utils/errorHandling';
import ErrorMessage, { FieldError } from '../ui/ErrorMessage';
import { CardLoadingSpinner } from '../ui/LoadingSpinner';
import { KatexPreview, KatexPreviewPanel } from '../ui/KatexPreview';
import ImageUpload from '../ui/ImageUpload';
import { getImageUrl } from '../../utils/imageUtils';
import { QUESTION_TYPES, DIFFICULTY_LEVELS, EXAM_TYPES, SUBJECTS, CLASS_LEVELS, getTopicsForSubject } from '../../constants/questionConstants';
import { renderLatex } from '../../lib/latex';

const EditQuestionModal = ({ isOpen, onClose, onSubmit, question }) => {

  const formRef = useRef(null);
  const errorRef = useRef(null);

  const [formData, setFormData] = useState({
    text: '',
    type: 'MCQ_SINGLE_CORRECT', // Default question type
    questionImageUrl: '',
    options: [
      { text: '', isCorrect: false, imageUrl: '' },
      { text: '', isCorrect: false, imageUrl: '' },
      { text: '', isCorrect: false, imageUrl: '' },
      { text: '', isCorrect: false, imageUrl: '' }
    ],
    numericalAnswer: '', // For FILL_IN_THE_BLANK_NUMERIC questions
    subject: '',
    topic: '',
    subtopic: '',
    difficulty: 'MEDIUM',
    accessScope: 'GLOBAL',
    kitId: '',
    tenantId: '',
    tags: [],
    examTypes: [],
    explanation: '',
    explanationImageUrl: '',
    reference: '',
    classLevel: '',
    passage: '',
    passageId: ''
  });

  const [tagInput, setTagInput] = useState('');
  const [examTypeInput, setExamTypeInput] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [showImageUploads, setShowImageUploads] = useState(false);

  // Data for dropdowns
  const [tenants, setTenants] = useState([]);
  const [kits, setKits] = useState([]);
  const [loadingData, setLoadingData] = useState(false);

  // Dynamic filter options
  const [difficultyOptions, setDifficultyOptions] = useState([]);
  const [subjectOptions, setSubjectOptions] = useState([]);
  const [topicOptions, setTopicOptions] = useState([]);
  const [subtopicOptions, setSubtopicOptions] = useState([]);
  const [examTypeOptions, setExamTypeOptions] = useState([]);
  const [questionTypeOptions, setQuestionTypeOptions] = useState([]);

  // Load dropdown data
  const loadDropdownData = async () => {
    setLoadingData(true);
    try {
      const [
        tenantsResponse,
        kitsResponse
      ] = await Promise.all([
        superAdminAPI.getAllTenants(),
        superAdminAPI.getAllKits()
      ]);

      // Use predefined constants for dropdown options
      setSubjectOptions(SUBJECTS);
      setDifficultyOptions(DIFFICULTY_LEVELS);
      setQuestionTypeOptions(QUESTION_TYPES);
      setExamTypeOptions(EXAM_TYPES);

      setTenants(tenantsResponse.data?.content || tenantsResponse.data || []);
      setKits(kitsResponse.data?.content || kitsResponse.data || []);

    } catch (error) {
      console.error('Error loading dropdown data:', error);
      // Set fallback data
      setTenants([
        { id: 'abc-university', name: 'ABC University' },
        { id: 'xyz-college', name: 'XYZ College' }
      ]);
      setKits([
        { id: 'math-basic', name: 'Mathematics Basic' },
        { id: 'physics-advanced', name: 'Physics Advanced' }
      ]);
      // Use predefined constants even on error
      setSubjectOptions(SUBJECTS);
      setDifficultyOptions(DIFFICULTY_LEVELS);
      setQuestionTypeOptions(QUESTION_TYPES);
      setExamTypeOptions(EXAM_TYPES);
    } finally {
      setLoadingData(false);
    }
  };

  // Load topics when subject changes
  const loadTopics = async (subject) => {
    try {
      const response = await superAdminAPI.getQuestionTopics(subject);
      if (response.data) {
        setTopicOptions(response.data);
      }
    } catch (error) {
      console.error('Error loading topics:', error);
      setTopicOptions([]);
    }
  };

  // Load subtopics when topic changes
  const loadSubtopics = async (subject, topic) => {
    try {
      const response = await superAdminAPI.getQuestionSubtopics(subject, topic);
      if (response.data) {
        setSubtopicOptions(response.data);
      }
    } catch (error) {
      console.error('Error loading subtopics:', error);
      setSubtopicOptions([]);
    }
  };

  // Load dropdown data and then populate form when modal opens
  useEffect(() => {
    if (isOpen) {
      clearErrors(setErrors);

      const initModal = async () => {
        await loadDropdownData();

        if (question) {
          // Normalize legacy MULTIPLE_CHOICE to MCQ_SINGLE_CORRECT
          const normalizedType = question.type === 'MULTIPLE_CHOICE'
            ? 'MCQ_SINGLE_CORRECT'
            : (question.type || 'MCQ_SINGLE_CORRECT');

          // Ensure the question's subject is in the dropdown options
          const questionSubject = question.subject || question.category || '';
          if (questionSubject && !SUBJECTS.includes(questionSubject)) {
            setSubjectOptions(prev => [...new Set([...prev, questionSubject])]);
          }

          // Ensure the question's difficulty is in the dropdown options
          const questionDifficulty = (question.difficultyLevel || question.difficulty || 'MEDIUM').toUpperCase();
          if (questionDifficulty && !DIFFICULTY_LEVELS.includes(questionDifficulty)) {
            setDifficultyOptions(prev => [...new Set([...prev, questionDifficulty])]);
          }

          // Process options to ensure correct answer is properly set
          let processedOptions = [];
          if (question.options && question.options.length > 0) {
            processedOptions = question.options.map((option, index) => ({
              text: option.text || '',
              imageUrl: option.imageUrl || '',
              isCorrect: option.isCorrect || option.correct ||
                        (question.correctAnswerIndex && (question.correctAnswerIndex === index + 1)) ||
                        (question.correctAnswer && question.correctAnswer === (index + 1).toString())
            }));
          } else {
            processedOptions = [
              { text: '', isCorrect: false, imageUrl: '' },
              { text: '', isCorrect: false, imageUrl: '' },
              { text: '', isCorrect: false, imageUrl: '' },
              { text: '', isCorrect: false, imageUrl: '' }
            ];
          }

          // Convert tags/examTypes from Set or null to array
          const tags = question.tags
            ? (Array.isArray(question.tags) ? question.tags : [...question.tags])
            : [];
          const examTypes = question.examTypes
            ? (Array.isArray(question.examTypes) ? question.examTypes : [...question.examTypes])
            : [];

          setFormData({
            text: question.questionText || question.text || '',
            type: normalizedType,
            options: processedOptions,
            numericalAnswer: question.correctAnswer || '',
            subject: questionSubject,
            topic: question.topic || '',
            subtopic: question.subtopic || '',
            difficulty: questionDifficulty,
            accessScope: question.accessScope || ((question.tenantId || (question.tenantCodes && question.tenantCodes.length > 0)) ? 'TENANT' : 'GLOBAL'),
            kitId: question.kitId || '',
            tenantId: question.tenantId || '',
            tags,
            examTypes,
            explanation: question.explanation || '',
            questionImageUrl: question.questionImageUrl || '',
            explanationImageUrl: question.explanationImageUrl || '',
            reference: question.reference || '',
            classLevel: question.classLevel || '',
            passage: question.passage || '',
            passageId: question.passageId || ''
          });
        }
      };

      initModal();
    }
  }, [isOpen, question, question?.updatedAt, question?.questionImageUrl, question?.explanationImageUrl]);

  // Set toggle state when modal opens or question changes
  useEffect(() => {
    if (isOpen && question) {
      const questionHasImages = !!(
        question.questionImageUrl ||
        question.explanationImageUrl ||
        (question.options && question.options.some(opt => opt.imageUrl))
      );

      setShowImageUploads(questionHasImages);
    } else if (isOpen && !question) {
      setShowImageUploads(false);
    }
  }, [isOpen, question?.id]); // Run when modal opens or question changes

  const resetForm = () => {
    setFormData({
      text: '',
      type: 'MCQ_SINGLE_CORRECT',
      questionImageUrl: '',
      options: [
        { text: '', isCorrect: false, imageUrl: '' },
        { text: '', isCorrect: false, imageUrl: '' },
        { text: '', isCorrect: false, imageUrl: '' },
        { text: '', isCorrect: false, imageUrl: '' }
      ],
      numericalAnswer: '',
      subject: '',
      topic: '',
      subtopic: '',
      difficulty: 'MEDIUM',
      accessScope: 'GLOBAL',
      kitId: '',
      tenantId: '',
      tags: [],
      examTypes: [],
      explanation: '',
      explanationImageUrl: '',
      reference: '',
      classLevel: '',
      passage: '',
      passageId: ''
    });
    setTagInput('');
    setExamTypeInput('');
    clearErrors(setErrors);
  };

  // Enhanced validation
  const validateForm = () => {
    const requiredFields = ['text', 'subject'];
    const fieldErrors = validateRequiredFields(formData, requiredFields);

    // Validate based on question type
    if (formData.type === 'FILL_IN_THE_BLANK_NUMERIC') {
      // Validate numerical answer
      if (!formData.numericalAnswer || !formData.numericalAnswer.trim()) {
        fieldErrors.numericalAnswer = 'Numerical answer is required';
      }
    } else {
      // Validate options for MCQ and TRUE_FALSE
      if (formData.options.some(option => !option.text.trim())) {
        fieldErrors.options = 'All answer options must be filled';
      }

      if (!formData.options.some(option => option.isCorrect)) {
        fieldErrors.correctAnswer = 'Please select the correct answer';
      }
    }

    // Validate topic if subject is provided
    if (formData.subject && !formData.topic.trim()) {
      fieldErrors.topic = 'Topic is required when subject is specified';
    }

    if (formData.accessScope === 'TENANT' && !formData.tenantId) {
      fieldErrors.tenantId = 'Select a tenant for tenant-specific questions';
    }

    setErrors(fieldErrors);
    return Object.keys(fieldErrors).length === 0;
  };

  const handleInputChange = (field, value) => {
    if (field === 'accessScope') {
      setFormData(prev => ({
        ...prev,
        accessScope: value,
        tenantId: value === 'GLOBAL' ? '' : prev.tenantId
      }));
      if (errors.tenantId) {
        setErrors(prev => ({ ...prev, tenantId: undefined }));
      }
      return;
    }

    if (field === 'tenantId') {
      setFormData(prev => ({
        ...prev,
        tenantId: value,
        accessScope: value ? 'TENANT' : 'GLOBAL'
      }));
      if (errors.tenantId) {
        setErrors(prev => ({ ...prev, tenantId: undefined }));
      }
      return;
    }

    // Handle question type changes
    if (field === 'type') {
      setFormData(prev => ({
        ...prev,
        type: value,
        // Reset form fields based on question type
        options: value === 'FILL_IN_THE_BLANK_NUMERIC' ? [] :
                value === 'TRUE_FALSE' ? [
                  { text: 'True', isCorrect: false, imageUrl: '' },
                  { text: 'False', isCorrect: false, imageUrl: '' }
                ] : [
                  { text: '', isCorrect: false, imageUrl: '' },
                  { text: '', isCorrect: false, imageUrl: '' },
                  { text: '', isCorrect: false, imageUrl: '' },
                  { text: '', isCorrect: false, imageUrl: '' }
                ],
        numericalAnswer: value === 'FILL_IN_THE_BLANK_NUMERIC' ? prev.numericalAnswer : ''
      }));
      return;
    }

    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Load topics when subject changes
    if (field === 'subject' && value) {
      loadTopics(value);
      // Clear topic and subtopic when subject changes
      setFormData(prev => ({
        ...prev,
        topic: '',
        subtopic: ''
      }));
      setSubtopicOptions([]);
    }

    // Load subtopics when topic changes
    if (field === 'topic' && value) {
      loadSubtopics(formData.subject, value);
      // Clear subtopic when topic changes
      setFormData(prev => ({
        ...prev,
        subtopic: ''
      }));
    }

    // Clear field-specific error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };

  const handleOptionChange = (index, value) => {
    const newOptions = [...formData.options];
    newOptions[index].text = value;
    setFormData(prev => ({
      ...prev,
      options: newOptions
    }));

    // Clear options error when user starts typing
    if (errors.options) {
      setErrors(prev => ({ ...prev, options: undefined }));
    }
  };

  const handleOptionImageChange = (index, imageUrl) => {
    const newOptions = [...formData.options];
    newOptions[index].imageUrl = imageUrl;

    // Add automatic text hint when option image is uploaded
    if (imageUrl && !newOptions[index].text.includes('[See Image]')) {
      const currentText = newOptions[index].text.trim();
      newOptions[index].text = currentText ? `${currentText} [See Image]` : '[See Image]';
    }

    setFormData(prev => ({
      ...prev,
      options: newOptions
    }));
  };

  // Enhanced handlers for automatic text hints when images are uploaded
  const handleQuestionImageChange = (imageUrl) => {
    handleInputChange('questionImageUrl', imageUrl || '');

    // Add automatic text hint when image is uploaded
    if (imageUrl && !formData.text.includes('[See Image]')) {
      const currentText = formData.text.trim();
      const newText = currentText ? `${currentText} [See Image]` : '[See Image]';
      handleInputChange('text', newText);
    }
  };

  const handleExplanationImageChange = (imageUrl) => {
    handleInputChange('explanationImageUrl', imageUrl || '');

    // Add automatic text hint when explanation image is uploaded
    if (imageUrl && !formData.explanation.includes('[See Image]')) {
      const currentText = formData.explanation.trim();
      const newText = currentText ? `${currentText} [See Image]` : '[See Image]';
      handleInputChange('explanation', newText);
    }
  };

  const handleCorrectOptionChange = (index) => {
    const newOptions = formData.options.map((option, i) => ({
      ...option,
      isCorrect: i === index
    }));
    setFormData(prev => ({
      ...prev,
      options: newOptions
    }));

    // Clear correct answer error when user selects an option
    if (errors.correctAnswer) {
      setErrors(prev => ({ ...prev, correctAnswer: undefined }));
    }
  };

  const addTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim()]
      }));
      setTagInput('');
    }
  };

  const removeTag = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const addExamType = () => {
    if (examTypeInput.trim() && !formData.examTypes.includes(examTypeInput.trim())) {
      setFormData(prev => ({
        ...prev,
        examTypes: [...prev.examTypes, examTypeInput.trim()]
      }));
      setExamTypeInput('');
    }
  };

  const removeExamType = (typeToRemove) => {
    setFormData(prev => ({
      ...prev,
      examTypes: prev.examTypes.filter(type => type !== typeToRemove)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Clear previous errors
    clearErrors(setErrors);

    // Validate form
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      let correctAnswer;

      if (formData.type === 'FILL_IN_THE_BLANK_NUMERIC') {
        // For numerical questions, use the numerical answer directly
        correctAnswer = formData.numericalAnswer;
      } else {
        // For MCQ and TRUE_FALSE, derive correct answer from options
        const correctOptions = formData.options
          .map((option, index) => option.isCorrect ? (index + 1).toString() : null)
          .filter(Boolean);
        correctAnswer = correctOptions.join(',');
      }

      const selectedTenant = tenants.find(tenant => tenant.id === formData.tenantId);

      // Transform form data to match API expectations
      const submitData = {
        ...formData,
        questionText: formData.text, // Map text to questionText
        difficultyLevel: formData.difficulty, // Map difficulty to difficultyLevel
        correctAnswer: correctAnswer, // Use appropriate correct answer based on type
        category: formData.subject, // Map subject to category for API compatibility
        accessScope: formData.accessScope,
        tenantId: formData.accessScope === 'TENANT' ? (formData.tenantId || null) : null,
        tenantCodes: formData.accessScope === 'TENANT' && selectedTenant
          ? [selectedTenant.code || selectedTenant.name || selectedTenant.id]
          : [],
        // For numerical questions, clear options array
        options: formData.type === 'FILL_IN_THE_BLANK_NUMERIC' ? [] : formData.options
      };



      await onSubmit(question.id, submitData);
      onClose();
    } catch (error) {
      console.error('Error updating question:', error);
      handleFormError(error, setErrors, formRef, errorRef);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white modal-responsive max-w-7xl w-full max-h-[95vh] overflow-y-auto shadow-xl">
        <div className="flex items-center justify-between p-6 border-b border-slate-200">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
              <AlertCircle className="h-4 w-4 text-blue-600" />
            </div>
            <h2 className="text-xl font-semibold azure-text">Edit Question</h2>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 transition-colors duration-200"
            disabled={isSubmitting}
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {loadingData && (
          <div className="p-6">
            <CardLoadingSpinner message="Loading form data..." />
          </div>
        )}

        {!loadingData && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 p-6">
            {/* Form Section */}
            <div className="space-y-6">
              <form ref={formRef} onSubmit={handleSubmit} className="space-y-6">
                {/* Error Message */}
                <ErrorMessage
                  ref={errorRef}
                  error={errors.general}
                  errors={errors}
                  onClear={() => clearErrors(setErrors)}
                  className="mb-6"
                />
            {/* Question Text */}
            <div className="field-text">
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Question Text *
              </label>
              <textarea
                name="text"
                value={formData.text}
                onChange={(e) => handleInputChange('text', e.target.value)}
                className={`input-field h-32 resize-none ${errors.text ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}`}
                placeholder="Enter the question text..."
                required
              />
              <FieldError error={errors.text} />
            </div>

            {/* Question Type */}
            <div className="field-type">
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Question Type *
              </label>
              <select
                name="type"
                value={formData.type}
                onChange={(e) => handleInputChange('type', e.target.value)}
                className="input-field"
                required
              >
                {questionTypeOptions.map(type => (
                  <option key={type} value={type}>
                    {type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </option>
                ))}
              </select>
            </div>

            {/* Passage fields for Reading Comprehension */}
            {formData.type === 'READING_COMPREHENSION' && (
              <div className="space-y-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <div>
                  <label className="block text-sm font-medium text-amber-800 mb-2">
                    Passage ID *
                  </label>
                  <input
                    type="text"
                    name="passageId"
                    value={formData.passageId}
                    onChange={(e) => handleInputChange('passageId', e.target.value)}
                    className="input-field"
                    placeholder="Unique ID to group sub-questions (e.g., PASSAGE_001)"
                    required
                  />
                  <p className="text-xs text-amber-700 mt-1">
                    All sub-questions sharing this ID will be grouped under the same passage
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-amber-800 mb-2">
                    Passage Text
                  </label>
                  <textarea
                    name="passage"
                    value={formData.passage}
                    onChange={(e) => handleInputChange('passage', e.target.value)}
                    className="input-field h-32 resize-y"
                    placeholder="Enter the passage text (shared across all sub-questions with the same Passage ID)"
                  />
                  <p className="text-xs text-amber-700 mt-1">
                    The passage only needs to be entered on one sub-question; others with the same Passage ID will share it
                  </p>
                </div>
              </div>
            )}

            {/* Image Upload Toggle */}
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border">
              <div>
                <label className="text-sm font-medium text-slate-700">
                  Enable Image Uploads
                </label>
                <p className="text-xs text-slate-500 mt-1">
                  Show image upload options for question, options, and explanation
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={showImageUploads}
                  onChange={(e) => setShowImageUploads(e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
            </div>

            {/* Question Image */}
            {showImageUploads && (
              <ImageUpload
                label="Question Image (Optional)"
                value={formData.questionImageUrl}
                onChange={handleQuestionImageChange}
                entityId={`question-edit-${question?.id || Date.now()}`}
                imageType="QUESTION"
              />
            )}

            {/* Options - Only for MCQ and TRUE_FALSE questions */}
            {(formData.type.startsWith('MCQ') || formData.type === 'TRUE_FALSE') && (
            <div className="field-options">
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Answer Options *
              </label>
              <div className="space-y-4">
                {formData.options.map((option, index) => (
                  <div key={index} className="space-y-3">
                    <div className="flex items-center space-x-3">
                      <div className="flex items-center">
                        <input
                          type="radio"
                          name="correctOption"
                          checked={option.isCorrect}
                          onChange={() => handleCorrectOptionChange(index)}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-slate-300"
                        />
                        <span className="ml-2 text-sm font-medium text-slate-700 w-6">
                          {index + 1}
                        </span>
                      </div>
                      <input
                        type="text"
                        value={option.text}
                        onChange={(e) => handleOptionChange(index, e.target.value)}
                        className={`input-field flex-1 ${errors.options ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}`}
                        placeholder={`Option ${index + 1}`}
                        required
                      />
                    </div>

                    {/* Option Image Upload */}
                    {showImageUploads && (
                      <div className="ml-6">
                        <ImageUpload
                          label={`Option ${index + 1} Image (Optional)`}
                          value={option.imageUrl}
                          onChange={(imageUrl) => handleOptionImageChange(index, imageUrl)}
                          entityId={`option-edit-${index}-${question?.id || Date.now()}`}
                          imageType="OPTION"
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <div className="mt-2">
                <FieldError error={errors.options} />
                <FieldError error={errors.correctAnswer} />
                <p className="text-xs text-slate-500 mt-1">
                  Select the radio button next to the correct answer
                </p>
              </div>
            </div>
            )}

            {/* Numerical Answer - Only for FILL_IN_THE_BLANK_NUMERIC questions */}
            {formData.type === 'FILL_IN_THE_BLANK_NUMERIC' && (
            <div className="field-numerical-answer">
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Numerical Answer *
              </label>
              <input
                type="text"
                name="numericalAnswer"
                value={formData.numericalAnswer || ''}
                onChange={(e) => handleInputChange('numericalAnswer', e.target.value)}
                className={`input-field ${errors.numericalAnswer ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}`}
                placeholder="Enter the numerical answer"
                required
              />
              <FieldError error={errors.numericalAnswer} />
              <p className="text-xs text-slate-500 mt-1">
                Enter the correct numerical answer for this question
              </p>
            </div>
            )}

            {/* Subject, Topic, and Subtopic */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="field-subject">
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Subject *
                </label>
                <select
                  name="subject"
                  value={formData.subject}
                  onChange={(e) => handleInputChange('subject', e.target.value)}
                  className={`input-field ${errors.subject ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}`}
                  required
                >
                  <option value="">Select Subject</option>
                  {subjectOptions.map(subject => (
                    <option key={subject} value={subject}>{subject}</option>
                  ))}
                </select>
                <FieldError error={errors.subject} />
              </div>

              <div className="field-topic">
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Topic *
                </label>
                <input
                  type="text"
                  name="topic"
                  value={formData.topic}
                  onChange={(e) => handleInputChange('topic', e.target.value)}
                  className={`input-field ${errors.topic ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}`}
                  placeholder="Enter topic"
                  required
                />
                <FieldError error={errors.topic} />
              </div>

              <div className="field-subtopic">
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Subtopic
                </label>
                <input
                  type="text"
                  name="subtopic"
                  value={formData.subtopic}
                  onChange={(e) => handleInputChange('subtopic', e.target.value)}
                  className="input-field"
                  placeholder="Enter subtopic (optional)"
                />
              </div>
            </div>

            {/* Difficulty and Tenant/Kit */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="field-difficulty">
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Difficulty *
                </label>
                <select
                  name="difficulty"
                  value={formData.difficulty}
                  onChange={(e) => handleInputChange('difficulty', e.target.value)}
                  className="input-field"
                  required
                >
                  {difficultyOptions.map(difficulty => (
                    <option key={difficulty} value={difficulty}>
                      {difficulty.charAt(0) + difficulty.slice(1).toLowerCase()}
                    </option>
                  ))}
                </select>
              </div>

              <div className="field-classLevel">
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Class (Optional)
                </label>
                <select
                  name="classLevel"
                  value={formData.classLevel}
                  onChange={(e) => handleInputChange('classLevel', e.target.value)}
                  className="input-field"
                >
                  <option value="">Select Class</option>
                  {CLASS_LEVELS.map(level => (
                    <option key={level} value={level}>Class {level}</option>
                  ))}
                </select>
              </div>

              <div className="field-tenantId">
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Access Scope
                </label>
                <select
                  name="accessScope"
                  value={formData.accessScope}
                  onChange={(e) => handleInputChange('accessScope', e.target.value)}
                  className="input-field"
                >
                  <option value="GLOBAL">Global - available to all admin tenants</option>
                  <option value="TENANT">Tenant-specific - restricted to one tenant</option>
                </select>
                <p className="text-xs text-slate-500 mt-1">
                  Super admin can move a question between global access and a tenant-specific audience.
                </p>
              </div>

              <div className="field-tenantId">
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Target Tenant
                </label>
                <select
                  name="tenantId"
                  value={formData.tenantId}
                  onChange={(e) => handleInputChange('tenantId', e.target.value)}
                  className={`input-field ${errors.tenantId ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}`}
                  disabled={formData.accessScope !== 'TENANT'}
                >
                  <option value="">Select tenant</option>
                  {tenants.map(tenant => (
                    <option key={tenant.id} value={tenant.id}>
                      {tenant.name || tenant.code} ({tenant.code || tenant.id})
                    </option>
                  ))}
                </select>
                <FieldError error={errors.tenantId} />
                <p className="text-xs text-slate-500 mt-1">
                  {formData.accessScope === 'TENANT'
                    ? 'The selected tenant code will be saved with this question.'
                    : 'Not required for global questions.'}
                </p>
              </div>

              <div className="field-kitId">
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Kit (Optional)
                </label>
                <input
                  type="text"
                  name="kitId"
                  value={formData.kitId}
                  onChange={(e) => handleInputChange('kitId', e.target.value)}
                  className={`input-field ${errors.kitId ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}`}
                  placeholder="Enter or search kit name..."
                  list="kit-suggestions"
                />
                <datalist id="kit-suggestions">
                  {kits.map(kit => (
                    <option key={kit.id} value={kit.name || kit.title} />
                  ))}
                </datalist>
                <FieldError error={errors.kitId} />
                <p className="text-xs text-slate-500 mt-1">
                  Type to search existing kits or enter a new kit name. Leave empty to auto-generate.
                </p>
              </div>
            </div>

            {/* Exam Types */}
            <div className="field-examTypes">
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Exam Types
              </label>
              <div className="flex flex-wrap gap-2 mb-2">
                {formData.examTypes.map((examType, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-purple-100 text-purple-800"
                  >
                    {examType.replace(/_/g, ' ')}
                    <button
                      type="button"
                      onClick={() => removeExamType(examType)}
                      className="ml-2 text-purple-600 hover:text-purple-800"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
              <div className="flex space-x-2">
                <select
                  value={examTypeInput}
                  onChange={(e) => setExamTypeInput(e.target.value)}
                  className="input-field flex-1"
                >
                  <option value="">Select exam type to add</option>
                  {examTypeOptions.filter(type => !formData.examTypes.includes(type)).map(type => (
                    <option key={type} value={type}>
                      {type.replace(/_/g, ' ')}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={addExamType}
                  className="btn-secondary"
                  disabled={!examTypeInput}
                >
                  <Plus className="h-4 w-4" />
                </button>
              </div>
            </div>

            {/* Tags */}
            <div className="field-tags">
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Tags
              </label>
              <div className="flex flex-wrap gap-2 mb-2">
                {formData.tags.map((tag, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800"
                  >
                    {tag}
                    <button
                      type="button"
                      onClick={() => removeTag(tag)}
                      className="ml-2 text-blue-600 hover:text-blue-800"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
              <div className="flex space-x-2">
                <input
                  type="text"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                  className="input-field flex-1"
                  placeholder="Enter tag and press Enter"
                />
                <button
                  type="button"
                  onClick={addTag}
                  className="btn-secondary"
                  disabled={!tagInput.trim()}
                >
                  <Plus className="h-4 w-4" />
                </button>
              </div>
            </div>

            {/* Reference */}
            <div className="field-reference">
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Reference
              </label>
              <input
                type="text"
                name="reference"
                value={formData.reference}
                onChange={(e) => handleInputChange('reference', e.target.value)}
                className="input-field"
                placeholder="Book, article, chapter reference (optional)"
              />
              <p className="text-xs text-slate-500 mt-1">
                Source reference where this question is inspired from
              </p>
            </div>

            {/* Explanation */}
            <div className="field-explanation">
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Explanation
              </label>
              <textarea
                name="explanation"
                value={formData.explanation}
                onChange={(e) => handleInputChange('explanation', e.target.value)}
                className="input-field h-24 resize-none"
                placeholder="Enter explanation for the correct answer (optional)"
              />
              <p className="text-xs text-slate-500 mt-1">
                Provide a detailed explanation for why the selected answer is correct
              </p>
            </div>

            {/* Explanation Image */}
            {showImageUploads && (
              <ImageUpload
                label="Explanation Image (Optional)"
                value={formData.explanationImageUrl}
                onChange={handleExplanationImageChange}
                entityId={`explanation-edit-${question?.id || Date.now()}`}
                imageType="EXPLANATION"
              />
            )}

                {/* Submit Buttons */}
                <div className="flex items-center justify-end space-x-3 pt-6 border-t border-slate-200">
                  <button
                    type="button"
                    onClick={onClose}
                    className="btn-secondary"
                    disabled={isSubmitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn-primary"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Updating...' : 'Update Question'}
                  </button>
                </div>
              </form>
            </div>

            {/* Preview Section */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-slate-800">Live Preview</h3>

              {/* Question Preview */}
              <div className="border border-slate-200 rounded-lg bg-white">
                <div className="p-3 border-b border-slate-200 bg-slate-50">
                  <h4 className="text-sm font-medium text-slate-700">Question Preview</h4>
                </div>
                <div className="p-4">
                  {formData.text?.trim() ? (
                    <div className="text-sm leading-relaxed">
                      {renderLatex(formData.text)}
                    </div>
                  ) : (
                    <div className="text-slate-400 italic text-sm">
                      Question text preview will appear here...
                    </div>
                  )}
                  {formData.questionImageUrl && (
                    <div className="mt-3">
                      <img
                        src={formData.questionImageUrl}
                        alt="Question"
                        className="max-w-full h-auto rounded-lg shadow-sm max-h-64"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Answer Preview - Conditional based on question type */}
              {formData.type === 'FILL_IN_THE_BLANK_NUMERIC' ? (
                <div className="border border-slate-200 rounded-lg bg-white">
                  <div className="p-3 border-b border-slate-200 bg-slate-50">
                    <h4 className="text-sm font-medium text-slate-700">Answer Preview</h4>
                  </div>
                  <div className="p-4">
                    <div className="p-3 bg-blue-50 border border-blue-200 rounded">
                      <div className="text-sm">
                        <span className="font-medium">Numerical Answer: </span>
                        {formData.numericalAnswer || <span className="text-slate-400 italic">No answer provided</span>}
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                formData.options && formData.options.length > 0 && formData.options.some(opt => opt.text?.trim()) && (
                  <div className="border border-slate-200 rounded-lg bg-white">
                    <div className="p-3 border-b border-slate-200 bg-slate-50">
                      <h4 className="text-sm font-medium text-slate-700">Options Preview</h4>
                    </div>
                    <div className="p-4 space-y-2">
                      {formData.options.map((option, index) => (
                        option.text?.trim() && (
                          <div key={index} className={`p-2 rounded border ${option.isCorrect ? 'bg-green-50 border-green-200' : 'bg-slate-50 border-slate-200'}`}>
                            <div className="flex items-start space-x-2">
                              <span className="text-xs font-medium text-slate-500 mt-0.5">
                                {index + 1}.
                              </span>
                              <div className="flex-1 text-sm">
                                {renderLatex(option.text)}
                              </div>
                              {option.isCorrect && (
                                <span className="text-xs text-green-600 font-medium">✓ Correct</span>
                              )}
                            </div>
                            {option.imageUrl && (
                              <div className="mt-2 ml-6">
                                <img
                                  src={option.imageUrl}
                                  alt={`Option ${index + 1}`}
                                  className="max-w-full h-auto rounded shadow-sm max-h-32"
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                  }}
                                />
                              </div>
                            )}
                          </div>
                        )
                      ))}
                    </div>
                  </div>
                )
              )}

              {/* Explanation Preview */}
              {formData.explanation?.trim() && (
                <div className="border border-slate-200 rounded-lg bg-white">
                  <div className="p-3 border-b border-slate-200 bg-slate-50">
                    <h4 className="text-sm font-medium text-slate-700">Explanation Preview</h4>
                  </div>
                  <div className="p-4">
                    <div className="text-sm leading-relaxed">
                      {renderLatex(formData.explanation)}
                    </div>
                    {formData.explanationImageUrl && (
                      <div className="mt-3">
                        <img
                          src={formData.explanationImageUrl}
                          alt="Explanation"
                          className="max-w-full h-auto rounded-lg shadow-sm max-h-64"
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EditQuestionModal;
